import apiClient from './api'
import { DEFAULT_PAGE_SIZE } from '@/lib/listFetch'
import type {
  ApiResponse,
  ApiList,
  Task,
  TaskDetail,
  TaskListQuery,
  TaskSummary,
  CreateTaskRequest,
  EditTaskRequest,
  UpdateTaskStatusRequest,
  TaskChecklistItem,
  CreateChecklistItemRequest,
  UpdateChecklistItemRequest,
  TaskComment,
  CreateTaskCommentRequest,
  TaskFile,
} from '@/types'

type TaskListOptions = {
  paginate?: boolean
}

// 1:1 with Schema_Specification Section 4.9 (tasks / 2.9.x).
export const tasksApi = {
  // 2.9.1
  list: (q: TaskListQuery = {}, { paginate = true }: TaskListOptions = {}) => {
    const params: TaskListQuery = { ...q }
    if (paginate) {
      params.page = q.page ?? 1
      params.limit = q.limit ?? DEFAULT_PAGE_SIZE
    } else {
      delete params.page
      delete params.limit
    }
    return apiClient.get<ApiList<Task>>('/tasks', { params }).then(r => r.data)
  },

  // 2.9.1 - KPI summary cards. Kept separate from list metadata/filters.
  summary: () =>
    apiClient.get<ApiResponse<TaskSummary>>('/tasks/summary').then(r => r.data),

  // 2.9.3
  get: (id: string) =>
    apiClient.get<ApiResponse<TaskDetail>>(`/tasks/${id}`).then(r => r.data),

  // 2.9.2
  create: (body: CreateTaskRequest) =>
    apiClient.post<ApiResponse<Task>>('/tasks', body).then(r => r.data),

  // 2.9.4
  update: (id: string, body: EditTaskRequest) =>
    apiClient.put<ApiResponse<Task>>(`/tasks/${id}`, body).then(r => r.data),

  // 2.9.5
  updateStatus: (id: string, body: UpdateTaskStatusRequest) =>
    apiClient
      .put<ApiResponse<Task>>(`/tasks/${id}/status`, body)
      .then(r => r.data),

  // 2.9.6
  remove: (id: string) =>
    apiClient.delete<ApiResponse<null>>(`/tasks/${id}`).then(r => r.data),

  // --- To-do checklist (feedback #69) ---
  listChecklist: (taskId: string) =>
    apiClient
      .get<ApiResponse<TaskChecklistItem[]>>(`/tasks/${taskId}/checklist-items`)
      .then(r => r.data),

  addChecklistItem: (taskId: string, body: CreateChecklistItemRequest) =>
    apiClient
      .post<
        ApiResponse<TaskChecklistItem>
      >(`/tasks/${taskId}/checklist-items`, body)
      .then(r => r.data),

  updateChecklistItem: (
    taskId: string,
    itemId: string,
    body: UpdateChecklistItemRequest
  ) =>
    apiClient
      .put<
        ApiResponse<TaskChecklistItem>
      >(`/tasks/${taskId}/checklist-items/${itemId}`, body)
      .then(r => r.data),

  removeChecklistItem: (taskId: string, itemId: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/tasks/${taskId}/checklist-items/${itemId}`)
      .then(r => r.data),

  // --- Comments (feedback #71, add-only) ---
  listComments: (taskId: string) =>
    apiClient
      .get<ApiResponse<TaskComment[]>>(`/tasks/${taskId}/comments`)
      .then(r => r.data),

  addComment: (taskId: string, body: CreateTaskCommentRequest) =>
    apiClient
      .post<ApiResponse<TaskComment>>(`/tasks/${taskId}/comments`, body)
      .then(r => r.data),

  // --- Attachments (feedback #70) ---
  listFiles: (taskId: string) =>
    apiClient
      .get<ApiResponse<TaskFile[]>>(`/tasks/${taskId}/files`)
      .then(r => r.data),

  uploadFile: (taskId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return apiClient
      .post<ApiResponse<TaskFile>>(`/tasks/${taskId}/files`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(r => r.data)
  },

  removeFile: (taskId: string, fileId: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/tasks/${taskId}/files/${fileId}`)
      .then(r => r.data),

  renameFile: (taskId: string, fileId: string, name: string) =>
    apiClient
      .patch<
        ApiResponse<TaskFile>
      >(`/tasks/${taskId}/files/${fileId}/rename`, { name })
      .then(r => r.data),
}

export default tasksApi
